import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LegalShell, SUPPORT_EMAIL } from '@/components/legal/LegalShell';
import { DeletionPanel } from '@/components/account/DeletionPanel';
import { GoogleAuthButton } from '@/components/GoogleAuthButton';

const H = ({ children }) => <h2 className="font-display text-lg font-bold text-white">{children}</h2>;
const UL = ({ items }) => <ul className="ml-5 list-disc space-y-1.5 marker:text-brand">{items.map((i) => <li key={i}>{i}</li>)}</ul>;

function Action() {
  const { session, loading } = useAuth();
  if (loading) return null;
  if (session) return <DeletionPanel />;
  return (
    <div className="space-y-3" data-testid="delete-account-login-required">
      <p className="text-sm text-white/80">Connecte-toi pour confirmer ton identité et initier la demande.</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Link to="/login?next=/delete-account" data-testid="delete-account-login"
              className="flex-1 rounded-full bg-brand py-3 text-center font-semibold text-black btn-glow">Se connecter par e-mail</Link>
        <div className="flex-1"><GoogleAuthButton next="/delete-account" testId="delete-account-google" /></div>
      </div>
    </div>
  );
}

export default function DeleteAccount() {
  return (
    <LegalShell title="Suppression de compte" subtitle="MyMusic IA — procédure Web et Android" testId="delete-account-page">
      <div className="space-y-8 text-sm leading-relaxed text-white/80 sm:text-base">
        <section className="space-y-3">
          <H>Comment demander la suppression</H>
          <p>Depuis les paramètres de ton compte (site web ou application mobile), ou directement sur cette page après connexion. Tu confirmes ton identité, puis tu valides la demande.</p>
        </section>
        <section className="space-y-3">
          <H>Délai de récupération de 30 jours</H>
          <UL items={['Le compte est placé en suppression programmée.', "L'accès ordinaire au service est suspendu.",
            "Tu peux annuler la demande pendant 30 jours en te reconnectant.", "À l'expiration du délai, la suppression définitive est exécutée."]} />
        </section>
        <section className="space-y-3">
          <H>Données supprimées et conséquences</H>
          <UL items={['Données personnelles du profil (pseudo, nom, e-mail, photo).', "Informations d'authentification MyMusic IA (e-mail / mot de passe et liaison Google).",
            'Créations, y compris les musiques publiées dans les challenges, et fichiers audio associés.', "Likes, demandes d'assistance et historique de crédits.",
            'Les crédits non utilisés sont perdus, sous réserve des droits applicables.']} />
        </section>
        <section className="space-y-3">
          <H>Exceptions légales</H>
          <p>Les données nécessaires à une obligation légale ou à un litige (par exemple les références des commandes payées) peuvent être conservées de manière limitée. Les statistiques d'écoute réellement anonymisées peuvent être conservées.</p>
        </section>
        <section className="space-y-3" data-testid="delete-songs-section">
          <H>Suppression de vos musiques</H>
          <p>Vous pouvez supprimer individuellement les musiques que vous avez créées depuis votre bibliothèque MyMusic IA, sans avoir à supprimer votre compte.</p>
          <p>Pour cela, connectez-vous à votre compte, ouvrez votre bibliothèque, sélectionnez la musique concernée et utilisez l'option de suppression.</p>
          <p>Cette action est définitive. La musique supprimée ne sera plus accessible depuis votre bibliothèque.</p>
        </section>
        <section className="space-y-3">
          <H>Contact</H>
          <p>Pour toute question : <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand hover:underline">{SUPPORT_EMAIL}</a>. Voir aussi la <Link to="/privacy-policy" className="text-brand hover:underline">Politique de confidentialité</Link>.</p>
        </section>
        <section className="card-surface space-y-4 p-6">
          <H>Initier la demande</H>
          <Action />
        </section>
      </div>
    </LegalShell>
  );
}
